import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { setCurrentTab, showNavBar, showSideBar } from '../../../actions/commonActions'
import { checkLicensePermission } from '../../../utility/communityLicense'
import ButtonLink from '../../common/buttons/ButtonLink'
import { Outlet, useLocation } from 'react-router-dom'
import { getCommunityCategories } from '../../../actions/programCategoryActions'
import { getCoachPrograms } from '../../../actions/programActions'
import { perPage, selectInitialFilters } from '../../../reducers/groupProgramsReducer'

import errorClasses from '../../common/LockError.module.scss'
import classes from './ProgramsPage.module.scss'
import { ReactComponent as PackageIcon } from '../../../assets/images/common/icons/package-icon.svg'

const ProgramsUnavailablePage = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const currentUser = useSelector(state => state.user.current_user)

  useEffect(() => {
    dispatch(setCurrentTab('Programs'))
    dispatch(showSideBar(true))
    dispatch(showNavBar(true))
  }, [dispatch])

  return (
    <div className={classes.programsPage}>
      <div className={classnames('d-flex flex-column justify-content-center align-items-center', errorClasses.wrapper)}>
        <div className={errorClasses.error}>
          <PackageIcon className={errorClasses.icon} />
          <h1 className={errorClasses.title}>{t('programs.groups.unavailable.title')}</h1>
          <p className={errorClasses.message}>
            {t('programs.groups.unavailable.message')}
          </p>
          {currentUser.lead_coach && (
            <ButtonLink
              navigateTo='/community-settings'
              className='communitySettingsLink'
            >
              {t('programs.groups.unavailable.goToCommunitySettings')}
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  )
}

const ProgramsPageWrapper = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const currentUser = useSelector(state => state.user.current_user)
  const initialFilters = useSelector(selectInitialFilters)

  const community = currentUser.communities[0]
  const permittedLicenseType = checkLicensePermission(community?.license_type, ['standard', 'standard_legacy', 'consumer_plan'])
  const stripePaymentsReady = community.selected_stripe_connected_account && community.available_packages_count > 0

  const [isLoadingCategories, setIsLoadingCategories] = useState(true)

  useEffect(() => {
    // Checking for category loading means not making the request duplicate when setting the loader to false
    // Checking for location pathname means not making the request when directly accessing a child subroute of /programs
    if ((permittedLicenseType || stripePaymentsReady) && isLoadingCategories && location.pathname === '/programs') {
      dispatch(getCommunityCategories()).unwrap()
        .then(() => setIsLoadingCategories(false))
    }
  }, [dispatch, isLoadingCategories, location.pathname, permittedLicenseType, stripePaymentsReady])

  useEffect(() => {
    if (!isLoadingCategories) {
      dispatch(getCoachPrograms({ filters: { ...initialFilters, active: 'true' }, page: 1, perPage: perPage, withEnrollments: 'true' }))
      dispatch(getCoachPrograms({ filters: { ...initialFilters, future: 'true' }, page: 1, perPage: perPage, withEnrollments: 'true' }))
      dispatch(getCoachPrograms({ filters: { draft: 'true' }, page: 1, perPage: perPage }))
      dispatch(getCoachPrograms({ filters: { ...initialFilters, completed: 'true' }, page: 1, perPage: perPage, withEnrollments: 'true' }))
    }
  }, [dispatch, initialFilters, isLoadingCategories])

  if (!permittedLicenseType && !stripePaymentsReady) {
    return <ProgramsUnavailablePage />
  }

  return <Outlet />
}

export default ProgramsPageWrapper
